import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { getVivaPnrFromPurchase } from 'selectors/viajeAhorrandoSelector';

/**
 * Hook that provides a factory function to create field disabling logic for passenger forms.
 * Returns a function that can be called inside .map() iterations since it's not a hook itself.
 *
 * @param {Object} options - Configuration options (values that don't change per passenger)
 * @param {boolean} options.isExchange - Whether the purchase is an exchange
 * @param {boolean} options.autofillEnabled - Whether autofill is enabled
 * @returns {Function} createIsDisabled - Factory function to create isDisabled per passenger
 *
 * @example
 * // Usage in DynamicPassengersForm (with .map())
 * const createIsDisabled = usePassengerFieldDisabled({ isExchange, autofillEnabled });
 *
 * const passengers = fields.map((passenger, index) => {
 *   const passengerFields = fields.get(index);
 *   const baseIsDisabled = createIsDisabled(passengerFields, index === 0);
 *   // Add component-specific conditions (like loyalty)
 *   const isDisabled = (field, fieldName) => baseIsDisabled(field, fieldName) || loyaltyCondition;
 * });
 *
 * @example
 * // Usage in PersonPassengersForm (without .map())
 * const createIsDisabled = usePassengerFieldDisabled({ isExchange, autofillEnabled });
 * const baseIsDisabled = createIsDisabled(passengerFields, isFirstPassenger);
 */
const usePassengerFieldDisabled = ({ isExchange, autofillEnabled }) => {
  const features = useWhitelabelFeatures();
  const vivaPnr = useSelector((state) => getVivaPnrFromPurchase(state));
  const hasViajaAhorrandoActive = Boolean(vivaPnr);

  /**
   * Factory function that creates an isDisabled function for a specific passenger.
   * Can be safely called inside .map() iterations.
   *
   * @param {Object} passengerFields - The passenger fields object (changes per passenger)
   * @param {boolean} isFirstPassenger - Whether this is the first passenger
   * @returns {Function} isDisabled - Function that checks if a field should be disabled
   */
  const createIsDisabled = useCallback(
    (passengerFields, isFirstPassenger) => {
      /**
       * Checks if a specific field should be disabled.
       *
       * @param {*} field - The current field value
       * @param {string} fieldName - The name of the field
       * @returns {boolean} True if the field should be disabled
       */
      return (field, fieldName) => {
        // Early return for exchange email fields
        // Email field should NOT be disabled when purchase is an exchange and field is empty
        if (isExchange && !field && fieldName === 'email') {
          return false;
        }

        // Exchange disabling condition
        // Fields are disabled when exchange passengers are not editable and field has a value
        const exchangeDisablingCondition = Boolean(
          isExchange && !features.EXCHANGE_PASSENGERS_EDITABLE && field,
        );

        // Viaja Ahorrando disabling condition
        // Fields are disabled when Viaja Ahorrando is active (vivaPnr in meta) and field has a value
        const vivaDisablingCondition = Boolean(hasViajaAhorrandoActive && field);

        // Autofill disabling condition
        // Fields are disabled when autofill is enabled, it's the first passenger, and field is not 'phone'
        const disableBecauseAutoFill = fieldName !== 'phone' && autofillEnabled && isFirstPassenger;

        // Email field disabling condition
        // Email field is disabled when feature flag SHOW_PASSENGERS_EMAIL_FIELD is false
        const enabledEmailField = fieldName === 'email' && !features.SHOW_PASSENGERS_EMAIL_FIELD;

        return (
          exchangeDisablingCondition ||
          vivaDisablingCondition ||
          disableBecauseAutoFill ||
          enabledEmailField
        );
      };
    },
    [
      isExchange,
      autofillEnabled,
      hasViajaAhorrandoActive,
      features.EXCHANGE_PASSENGERS_EDITABLE,
      features.SHOW_PASSENGERS_EMAIL_FIELD,
    ],
  );

  return createIsDisabled;
};

export default usePassengerFieldDisabled;
